import styled from "@emotion/styled";
import { Box, MenuItem, Select, type SelectChangeEvent, Typography } from "@mui/material";
import { HtmlParser } from "Components/_Shared/HtmlParser";
import { ReactComponent as ArrowUpIcon } from "Components/_Shared/Icons/Assets/arrow-up.svg";
import { StyledFormControl, StyledValidationText } from "Forms/Components/FormStyles";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import type { JSX } from "react";
import { type Control, Controller, type DeepMap, type FieldError, type FieldPath, type FieldValues } from "react-hook-form";

const StyledSelect = styled(Select)`
  width: 100%;
  height: 40px;
  border-radius: ${(props) => props.theme.spacing(Radius.M)};

  & fieldset {
    border-color: ${(props) => props.theme.colors.borderLight};
  }

&.Mui-disabled {
    background-color: #f5f5f5; // Změňte na požadovanou barvu
  }
`;

const StyledLabel = styled.div`
  display: flex;
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom:  ${(props) => props.theme.spacing(Spacing.S)};
`;

export type DropdownValueType = string | number | boolean;
export type Option = {
  readonly value: string;
  readonly name: string;
  readonly hidden?: boolean;
};

type Props<FormType extends FieldValues> = {
  name: FieldPath<FormType>;
  formControlClass?: "long" | "medium" | "tiny" | string;
  label?: string;
  control: Control<FormType, object>;
  errors: DeepMap<any, FieldError>;
  disabled?: boolean;
  hasFilterStyle?: boolean;
  multiple?: boolean;
  labelEndAdornment?: JSX.Element;
  hasValidationText?: boolean;
  options: Option[];
  onChange?: (value: string) => void;
};

const ArrowIcon = (props: any) => {
  return (
    <Box {...props} sx={{ transform: "rotate(180deg);" }} mt={-2} mr={Spacing.M}>
      <ArrowUpIcon />
    </Box>
  );
};

export const FormDropdown = <T extends object>(props: Props<T>) => {
  const {
    options,
    name,
    control,
    label,
    disabled,
    errors,
    formControlClass,
    hasFilterStyle,
    multiple,
    labelEndAdornment,
    onChange: propsOnChange,
    hasValidationText,
  } = props;
  const error: FieldError | undefined = errors[name];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: controllerOnChange, value, onBlur, name } }) => {
        const onChange = (event: SelectChangeEvent) => {
          const { value } = event.target;

          controllerOnChange(value);
          if (propsOnChange) {
            propsOnChange(value);
          }
        };

        return (
          <>
            <StyledFormControl
              className={`bl-form-control ${formControlClass ?? ""} ${!label ? "no-label" : ""} ${hasFilterStyle ? "filter" : ""}`}
            >
              {label && (
                <StyledLabel>
                  <Typography fontWeight={500} variant="body2" mr={Spacing.S}>
                    {label}
                  </Typography>
                  {labelEndAdornment}
                </StyledLabel>
              )}

              <StyledSelect
                value={value ?? ""}
                onChange={onChange as any}
                onBlur={onBlur}
                disabled={disabled}
                error={!!error}
                name={name}
                multiple={multiple}
                IconComponent={ArrowIcon}
                displayEmpty
                fullWidth
                MenuProps={{
                  PaperProps: {
                    elevation: 0,
                    sx: {
                      border: "1px solid",
                      borderColor: "grey.300",
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
                renderValue={
                  !value
                    ? () => (
                        <Typography lineHeight={1.5} variant="body1" color="secondary">
                          Vybrat
                        </Typography>
                      )
                    : undefined
                }
              >
                {options.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                      display: option.hidden ? "none" : "block",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      maxWidth: "450px",
                    }}
                  >
                    <HtmlParser content={option.name} />
                  </MenuItem>
                ))}
              </StyledSelect>
              {hasValidationText && (
                <StyledValidationText $hasFilterStyle={hasFilterStyle}>{!!error && error.message}</StyledValidationText>
              )}
            </StyledFormControl>
          </>
        );
      }}
    />
  );
};
